import { Box, Text, VStack } from '@chakra-ui/react';
import { Glyph } from '../../../ui/Glyph';
import type { GlyphRef } from '../../../../theme/glyph';
import { gradientVariantForGlyph } from '../../../../theme/iconGradient';

interface CreateTileProps {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly icon: GlyphRef;
  readonly shortcut?: string;
  readonly onSelect: () => void;
  readonly isDisabled?: boolean;
}

/** One create type on the modal's first step. */
export function CreateTile(props: CreateTileProps) {
  const { id, label, description, icon, shortcut, onSelect, isDisabled } = props;
  return (
    <VStack
      as="button"
      type="button"
      data-testid={`create-tile-${id}`}
      aria-keyshortcuts={shortcut}
      onClick={onSelect}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      opacity={isDisabled ? 0.6 : 1}
      cursor={isDisabled ? 'progress' : 'pointer'}
      position="relative"
      align="flex-start"
      spacing={1.5}
      minH={{ base: '96px', md: '112px' }}
      p={{ base: 3, md: 3.5 }}
      bg="bg.subtle"
      border="1px solid"
      borderColor="border.default"
      borderRadius="xl"
      textAlign="left"
      transitionProperty="background-color, border-color"
      transitionDuration="fast"
      _hover={{ bg: 'brand.muted', borderColor: 'brand.primary' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '2px' }}
    >
      <Glyph
        {...icon}
        size="24px"
        gradient={gradientVariantForGlyph(icon.name)}
        color="currentColor"
      />
      <Text fontSize="sm" fontWeight="semibold" color="text.primary">
        {label}
      </Text>
      <Text fontSize="xs" color="text.secondary" noOfLines={2}>
        {description}
      </Text>
      {shortcut && (
        <Box
          position="absolute"
          top={2.5}
          right={2.5}
          px={1.5}
          borderRadius="md"
          border="1px solid"
          borderColor="border.default"
          display={{ base: 'none', md: 'block' }}
          aria-hidden
        >
          <Text fontSize="2xs" color="text.secondary">
            {shortcut}
          </Text>
        </Box>
      )}
    </VStack>
  );
}
